import * as React from 'react'
import styled from 'styled-components'
import { IWindow } from 'static/interfaces'
import { agentService } from 'core/services'
import { Button, Spinner } from 'components/atoms'
import Typography from 'components/atoms/_layouts/Typography'
import { COLORS } from 'static/constants'
import DOMPurify from 'dompurify'

declare var window: IWindow

interface IProps {
  id: number
  connectionMailTemplateId: number | null
}

interface IMessagePreview {
  subject: string
  body: string
  missing_fields: string[]
}

export const ConnectionMessagePreviewModalBody = ({ id, connectionMailTemplateId }: IProps) => {
  const [loading, setLoading] = React.useState<boolean>(false)
  const [messagePreview, setMessagePreview] = React.useState<IMessagePreview>({
    subject: '',
    body: '',
    missing_fields: [],
  })

  React.useEffect(() => {
    const fetchMessage = async () => {
      try {
        setLoading(true)
        const { messagePreview: newMessagePreview } = await agentService.getConnectMessagePreview(
          id,
          connectionMailTemplateId
        )
        setMessagePreview(newMessagePreview)
      } catch (error) {
        console.error('Failed to fetch message preview:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMessage()
  }, [id, connectionMailTemplateId])

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      window.flashMessages.addMessage({
        text: 'コピーしました',
        type: 'success',
      })
    } catch (error) {
      window.flashMessages.addMessage({
        text: 'コピーに失敗しました',
        type: 'error',
      })
    }
  }

  return (
    <ConnectMessagePreviewModalBodyInner>
      {messagePreview.missing_fields.length > 0 && (
        <div className="warning">
          <Typography type="body" size="sm">
            {`不足情報があります: ${messagePreview.missing_fields.join(', ')}`}
          </Typography>
        </div>
      )}

      <div className="subtitle_wrapper">
        <Typography type="subtitle" size="xl">
          タイトル
        </Typography>
        <Button
          primary={false}
          small={true}
          disabled={loading}
          handleClick={() => copy(messagePreview.subject)}
        >
          コピー
        </Button>
      </div>

      <div className="message_preview_content">
        {loading ? <Spinner /> : <div className="message_preview">{messagePreview.subject}</div>}
      </div>

      <div className="subtitle_wrapper">
        <Typography type="subtitle" size="xl">
          本文
        </Typography>
        <Button
          primary={false}
          small={true}
          disabled={loading}
          handleClick={() => copy(messagePreview.body)}
        >
          コピー
        </Button>
      </div>

      <div className="message_preview_content">
        {loading ? (
          <Spinner />
        ) : (
          <div className="message_preview">
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(messagePreview.body) }} />
          </div>
        )}
      </div>
    </ConnectMessagePreviewModalBodyInner>
  )
}

const ConnectMessagePreviewModalBodyInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .message_preview_content {
    border: 1px solid ${COLORS.divider2};
    border-radius: 4px;
    margin-bottom: 16px;
    max-height: 400px;
    overflow: auto;
  }

  .message_preview {
    white-space: pre-wrap;
    word-break: break-all;
    padding: 16px;
  }

  .subtitle_wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .warning {
    background-color: ${COLORS.useallyBG};
    border: 1px solid ${COLORS.useally};
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 8px;
  }
`
